import { formatInteger, SUFFIX } from '@heviton/formatter';
/**
 * ISO datetime → `YYYY.MM.DD`.
 *
 * BE 의 기간 필드는 'UTC 자정 ISO Z'(예: `2026-06-15T00:00:00Z`)라 의도한 달력 날짜 = UTC 날짜다 →
 * **UTC 파트**로 포맷한다(로컬 타임존 변환으로 하루 밀리는 것 방지, 어디서나 결정적).
 * 빈/잘못된 입력은 빈 문자열로 격하(placeholder 는 소비 측이 결정).
 */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}.${month}.${day}`;
}

/**
 * ISO datetime → `YYYY.MM.DD HH:mm` (24h).
 *
 * 이벤트(알림) 시각은 BE 가 Asia/Seoul offset ISO(`...+09:00`)로 준다 → **KST 고정 표시**(디바이스
 * 타임존과 무관하게 BE 가 의도한 벽시계 시각, 어디서나 결정적). `formatDate`(기간=UTC 자정)와 용도가 다르다.
 * 빈/잘못된 입력은 빈 문자열로 격하.
 */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Seoul',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    })
      .formatToParts(date)
      .map((part) => [part.type, part.value]),
  );
  return `${parts.year}.${parts.month}.${parts.day} ${parts.hour}:${parts.minute}`;
}

/**
 * ISO datetime → 상대시간("방금 전"/"N분 전"/"N시간 전"/"N일 전"), 7일 초과는 `formatDate`(절대) 폴백.
 * 알림 최초발생 등 "얼마나 됐나"가 중요한 표시용. 절대 시각 차라 타임존 무관. 빈/잘못된 입력은 빈 문자열.
 */
export function formatRelativeTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const diffMs = new Date().getTime() - date.getTime();
  if (diffMs < 60_000) return '방금 전'; // 1분 미만 + 미래(시계 오차) 방어
  const min = Math.floor(diffMs / 60_000);
  if (min < 60) return `${min}분 전`;
  const hour = Math.floor(min / 60);
  if (hour < 24) return `${hour}시간 전`;
  const day = Math.floor(hour / 24);
  if (day < 7) return `${day}일 전`;
  return formatDate(iso);
}

// ---------------------------------------------------------------------------
// 날짜 범위/계산 유틸 (조회기간 등) — KST 기준. 표시 포맷(위)과 구분.
// ---------------------------------------------------------------------------

const DAY_MS = 24 * 60 * 60 * 1000;

// KST 날짜 포매터 — 모듈 싱글턴(호이스팅, admin 정합). 매 호출 new Intl 생성 회피.
const kstDateFormatter = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Seoul' });

/** `Date` → KST 기준 `yyyy-MM-dd`. 조회기간은 날짜 단위(Asia/Seoul). */
export function toKstDateString(date: Date): string {
  return kstDateFormatter.format(date);
}

/**
 * KST '오늘' → `'yyyy-MM-dd'`. 필터 기본 일자(계측·MPPT·스트링·이벤트 공용) SSOT(admin `getKstToday` 정합).
 * **함수**로 유지 — 모듈 상수로 굳히면 자정 넘긴 SPA 세션이 어제로 고정되므로 호출 시점(마운트/렌더)에 계산한다.
 */
export function getKstToday(): string {
  return kstDateFormatter.format(new Date());
}

/** 오늘 포함 최근 N일 범위(KST `yyyy-MM-dd`). 기본 조회기간·프리셋(7/30/90) 공용. */
export function lastNDaysRange(days: number): { startDate: string; endDate: string } {
  const today = new Date();
  const from = new Date(today.getTime() - (days - 1) * DAY_MS);
  return { startDate: toKstDateString(from), endDate: toKstDateString(today) };
}

/** 두 날짜(`yyyy-MM-dd`) 사이 일수(시작·종료 포함). 조회기간 상한 검증용. */
export function daysBetween(startDate: string, endDate: string): number {
  const start = new Date(`${startDate}T00:00:00Z`).getTime();
  const end = new Date(`${endDate}T00:00:00Z`).getTime();
  return Math.floor((end - start) / DAY_MS) + 1;
}

/**
 * `yyyy-MM-dd` 형식 + **실재 달력 날짜**인가. 형식 정규식만으로는 `2026-13-45` 같은 불가능한 날짜가 통과하므로
 * UTC 로 파싱해 round-trip 일치까지 확인한다(타임존 영향 없음). URL/폼 등 외부 입력 경계 검증용.
 */
export function isValidDateString(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  // 정규식이 자리수를 보장하므로 slice 로 안전 파싱(구조분해는 길이를 추론 못해 undefined 가능).
  const year = Number(value.slice(0, 4));
  const month = Number(value.slice(5, 7));
  const day = Number(value.slice(8, 10));
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
  );
}

/** 년 환산 기준 일수 — 윤년을 세지 않는 근사값. 가동일수는 「얼마나 오래 돌았나」 감각이라 하루 오차가 무의미하다. */
const DAYS_PER_YEAR = 365;

/**
 * 일수 → **년 + 나머지 일** 스케일업(`1,234` → `3년 139일`). 1년 미만이면 일만(`139일`).
 *
 * 가동일수는 값이 계속 커지는 축이라 `1,234일` 처럼 원단위로 두면 **자릿수만 늘고 감이 안 온다**(3년 남짓인지
 * 30년인지 한눈에 안 읽힌다). 상위 단위를 앞에 붙여 규모를 먼저 말하고, 나머지 일은 그대로 남겨 **정밀도를
 * 잃지 않는다**(`3.4년` 처럼 반올림하면 BE 가 준 실제 일수가 사라진다).
 *
 * 나머지가 0이면 년만(`3년`) — `3년 0일` 은 빈 자리를 채우는 소음이다.
 */
export function formatDaysAsYears(days: number | null | undefined): string {
  if (days == null || !Number.isFinite(days)) return '';
  const total = Math.max(0, Math.trunc(days));
  const years = Math.floor(total / DAYS_PER_YEAR);
  const rest = total % DAYS_PER_YEAR;
  if (years === 0) return formatInteger(rest, SUFFIX.DAY);
  if (rest === 0) return formatInteger(years, SUFFIX.YEAR);
  return `${formatInteger(years, SUFFIX.YEAR)} ${formatInteger(rest, SUFFIX.DAY)}`;
}
